import { tick } from "svelte";
import { init } from "svelte/internal";
export const FOCUSABLE_ELEMENTS = [
  "a[href]",
  "area[href]",
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  "select:not([disabled]):not([aria-hidden])",
  "textarea:not([disabled]):not([aria-hidden])",
  "button:not([disabled]):not([aria-hidden])",
  "iframe",
  "object",
  "embed",
  "[contenteditable]",
  '[tabindex]:not([tabindex^="-"])', // Include elements with positive tabindex
];

export function focusTrap(
  node,
  { focusableSelectors = FOCUSABLE_ELEMENTS.join(",") } = {}
) {
  let firstFocusableElement;
  let lastFocusableElement;
  let focusableElements;
  let observer;

  async function initializeFocusTrap() {
    await tick();
    observer = new MutationObserver((mutations) => {
      // Only update if content was added or removed
      if (mutations.some(m => m.addedNodes.length > 0 || m.removedNodes.length > 0)) {
        updateFocusableElements();
      }
    });

    observer.observe(node, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-hidden', 'tabindex'] 
    });
    updateFocusableElements();
  }

  function updateFocusableElements() {
    // Get all focusable elements and filter out the ones that are disabled or hidden
    focusableElements = Array.from(
      node.querySelectorAll(focusableSelectors)
    ).filter(
      (el) => {
        const style = window.getComputedStyle(el);
        return !el.hasAttribute("disabled") && 
               !el.hasAttribute("aria-hidden") &&
               style.display !== 'none' &&
               style.visibility !== 'hidden';
      }
    );

    // Sort by tabindex, preserving DOM order for elements with same tabindex
    focusableElements.sort((a, b) => {
      const aIndex = a.getAttribute("tabindex");
      const bIndex = b.getAttribute("tabindex");
      if (aIndex === null && bIndex === null) return 0;
      if (aIndex === null) return 1;
      if (bIndex === null) return -1;
      return parseInt(aIndex) - parseInt(bIndex);
    });

    firstFocusableElement = focusableElements[0];
    lastFocusableElement = focusableElements[focusableElements.length - 1];
  }

  function handleKeyDown(event) {
    if (event.key !== "Tab") return;

    // Always update focusable elements before handling tab
    updateFocusableElements();

    if (!focusableElements.length) return;

    const currentElement = document.activeElement;
    const currentIndex = focusableElements.indexOf(currentElement);
    
    if (event.shiftKey) {
      // If shift+tab
      if (currentIndex <= 0) {
        event.preventDefault();
        lastFocusableElement?.focus();
      } else if (currentIndex === -1) {
        event.preventDefault();
        lastFocusableElement?.focus();
      }
    } else {
      // If tab
      if (currentIndex === focusableElements.length - 1) {
        event.preventDefault();
        firstFocusableElement?.focus();
      } else if (currentIndex === -1) {
        event.preventDefault();
        firstFocusableElement?.focus();
      }
    }
  }

  initializeFocusTrap();
  node.addEventListener("keydown", handleKeyDown);
  
  return {
    update(newOptions) {
      if (
        newOptions.focusableSelectors &&
        newOptions.focusableSelectors !== focusableSelectors
      ) {
        focusableSelectors = newOptions.focusableSelectors;
        updateFocusableElements();
      }
    },
    destroy() {
      observer?.disconnect();
      node.removeEventListener("keydown", handleKeyDown);
    },
  };
}


/**
 * Fix the font family to be a valid CSS font family
 * @param fontFamily - The font family to fix
 * @returns A valid CSS font family
 */
export function fixFontFamily(fontFamily) {
  // split fontFamily into font names
  const fontNames = fontFamily.split(',').map(name => name.trim().replace(/['"]/g, ''));
  // remove duplicates
  const uniqueFontNames = [...new Set(fontNames)];
  // add quotes around the font names if they require it
  const fixedFontNames = uniqueFontNames.map(name => name.includes(' ') ? `"${name}"` : name);
  return fixedFontNames.join(',');
}

/**
 * Convert various video URLs to embed URLs
 * @param inputUrl - The input video URL
 * @param autoplay - Whether to enable autoplay (default: false)
 * @returns The embed URL or undefined if invalid
 */
export function makeEmbedUrl(inputUrl, autoplay = false) {
  if (!inputUrl) return;

  const autoplayValue = autoplay ? "1" : "0";
  const youtubeParams = `autoplay=${autoplayValue}&rel=0&showinfo=0&modestbranding=1&autohide=1&enablejsapi=1`;
  const vimeoParams = `autoplay=${autoplayValue}&title=0&byline=0&portrait=0`;

  try {
    // ===== SETUP & URL PREPARATION =====
    // if inputUrl has no protocol, add http:// to it
    if (inputUrl.startsWith("//")) {
      inputUrl = `http://${inputUrl}`;
    }

    const url = new URL(inputUrl);

    // ===== HELPER FUNCTIONS =====
    // YouTube: Extract timestamp from URL query or fragment
    // Supports formats: 34, 34s, 1m34s, 1h2m34s, etc.
    const getYouTubeTimestamp = (url) => {
      const timeParam = url.searchParams.get("t");
      if (!timeParam) {
        return '';
      }

      // Parse YouTube timestamp format (e.g., "34s", "1m34s", "1h2m34s", or just "34")
      let totalSeconds = 0;
      
      // Match hours, minutes, and seconds with their suffixes
      const hoursMatch = /(\d+)h/.exec(timeParam);
      const minutesMatch = /(\d+)m/.exec(timeParam);
      const secondsMatch = /(\d+)s/.exec(timeParam);
      
      if (hoursMatch?.[1]) {
        totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
      }
      if (minutesMatch?.[1]) {
        totalSeconds += parseInt(minutesMatch[1], 10) * 60;
      }
      if (secondsMatch?.[1]) {
        totalSeconds += parseInt(secondsMatch[1], 10);
      }
      
      // If no suffixes found, treat as plain seconds
      if (!hoursMatch && !minutesMatch && !secondsMatch) {
        const plainSeconds = parseInt(timeParam, 10);
        if (!isNaN(plainSeconds)) {
          totalSeconds = plainSeconds;
        }
      }
      
      return totalSeconds > 0 ? `&start=${totalSeconds}` : '';
    };

    // YouTube: Extract playlist ID if present
    const getYouTubePlaylist = (url) => {
      const playlistId = url.searchParams.get("list");
      return playlistId ? `&list=${playlistId}` : '';
    };

    // ===== EXISTING EMBED URL HANDLING =====
    // Handle existing YouTube embed URLs
    if (url.hostname.includes("youtube.com") && url.pathname === "/embed") {
      url.searchParams.set("autoplay", autoplayValue);
      return url.toString();
    }

    // Handle existing Vimeo embed URLs
    if (url.hostname.includes("player.vimeo.com")) {
      url.searchParams.set("autoplay", autoplayValue);
      return url.toString();
    }

    // Handle existing Wistia embed URLs (fast.wistia.net)
    if (url.hostname === "fast.wistia.net" && url.pathname.startsWith("/embed/iframe/")) {
      const mediaId = url.pathname.split("/")[4];
      if (mediaId) {
        return `https://fast.wistia.net/embed/iframe/${mediaId}?autoplay=${autoplayValue}`;
      }
    }

    // ===== YOUTUBE URL PROCESSING =====
    // Handle YouTube watch URLs
    if (url.hostname.includes("youtube.com") && url.pathname === "/watch") {
      const videoId = url.searchParams.get("v");
      if (videoId) {
        const timestamp = getYouTubeTimestamp(url);
        const playlist = getYouTubePlaylist(url);
        return `https://www.youtube.com/embed/${videoId}?${youtubeParams}${timestamp}${playlist}`;
      }
    }

    // Handle shortened YouTube URLs (youtu.be)
    if (url.hostname === "youtu.be") {
      const videoId = url.pathname.slice(1); // Remove leading "/"
      if (videoId) {
        const timestamp = getYouTubeTimestamp(url);
        const playlist = getYouTubePlaylist(url);
        return `https://www.youtube.com/embed/${videoId}?${youtubeParams}${timestamp}${playlist}`;
      }
    }

    // Handle YouTube Shorts URLs
    if (url.hostname.includes("youtube.com") && url.pathname.startsWith("/shorts/")) {
      const videoId = url.pathname.split("/")[2];
      if (videoId) {
        const timestamp = getYouTubeTimestamp(url);
        const playlist = getYouTubePlaylist(url);
        return `https://www.youtube.com/embed/${videoId}?${youtubeParams}${timestamp}${playlist}`;
      }
    }

    // ===== VIMEO URL PROCESSING =====
    // Handle Vimeo links
    if (url.hostname.includes("vimeo.com")) {
      const videoId = url.pathname.split("/")[1];
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}?${vimeoParams}${url.search ? `&${url.searchParams.toString()}` : ''}`;
      }
    }

    // ===== WISTIA URL PROCESSING =====
    // Handle Wistia media page URLs (e.g. https://camphorseshoe.wistia.com/medias/ctfzphpta9)
    if (url.hostname.endsWith("wistia.com") && url.pathname.startsWith("/medias/")) {
      const mediaId = url.pathname.split("/")[2];
      if (mediaId) {
        return `https://fast.wistia.net/embed/iframe/${mediaId}?autoplay=${autoplayValue}`;
      }
    }

    // ===== FALLBACK & ERROR HANDLING =====
    // Return the original URL if it's a valid HTTP(S) URL
    // But first, try to update autoplay parameter if it exists
    if (url.protocol.startsWith("http")) {
      // Check if this might be a video URL that could benefit from autoplay parameter
      if (url.pathname.toLowerCase().endsWith('.mp4') || 
          url.hostname.includes('youtube') || 
          url.hostname.includes('vimeo')) {
        // Try to update autoplay parameter if it exists
        if (url.searchParams.has('autoplay')) {
          url.searchParams.set('autoplay', autoplayValue);
          return url.toString();
        }
      }
      return inputUrl;
    }
  } catch (e) {
    // Invalid URL handling
    console.error("Invalid URL provided:", inputUrl, e);
    return undefined;
  }
}

/** Iconify CDN URL for popup direction icons (white fill). */
export function iconifyDirectionIconUrl(iconifyId, colorHex = "ffffff") {
  if (!iconifyId || typeof iconifyId !== "string") return null;
  const idx = iconifyId.indexOf(":");
  if (idx <= 0) return null;
  const prefix = iconifyId.slice(0, idx);
  const iconName = iconifyId.slice(idx + 1);
  if (!prefix || !iconName) return null;
  const hex = String(colorHex).replace("#", "");
  return `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(iconName)}.svg?color=%23${encodeURIComponent(hex)}`;
}